import {priority} from '@priority-software/priority-proto-api/lib';
import {TreeNodeProps} from '@priority-software/priority-style-react';
import {action, computed, observable, makeObservable} from 'mobx';
import texts from '../texts';
import {ColumnsFieldsNames, OpenExprDialogState, ReportColumn} from '../types';
import {prepareExpressionsToStore} from '../utils';
import {xAxisData} from '../componenets/FieldsTable/consts';
import {initOuterJoinMap} from '../actions';

export class Report {
    @observable columns: ReportColumn[] = [];
    @observable selectedNodes: Map<string, priority.netitems.nrest.ITreeNode>;
    @observable title: string = '';
    @observable isGeneral: boolean = false;
    @observable isActive: boolean = false;
    @observable isTabular: boolean = false;
    @observable isUseAsDataset: boolean = false;
    @observable isUseAsChart: boolean;
    @observable nodeId: string;
    @observable searchQuery: string;
    @observable isDirty: boolean;
    @observable reportId: number | Long;
    @observable isNew: boolean = false;
    @observable duplicateId: number | Long;
    @observable aXisModeData = xAxisData;
    @observable expressionIds: Array<number> = [];
    @observable outerJoinId: number;
    @observable openFunctionDialog: OpenExprDialogState = {
        index: null,
        column: null,
        isOpen: false
    };

    isVertical: boolean = false;
    linkId: string;
    outerJoinMap: TreeNodeProps[];
    savedOuterJoinId: number;

    constructor(nodeId: string, reportId?: number | Long) {
        makeObservable(this);
        this.nodeId = nodeId;
        this.reportId = reportId || 0;
        this.isNew = !reportId;
        this.isActive = !reportId;
        this.outerJoinMap = !reportId ? initOuterJoinMap(nodeId) : null;
    }

    @action
    setOpenFunctionDialog = (arg: OpenExprDialogState) => {
        this.openFunctionDialog = arg;
    };

    @action
    setReportData = (data: priority.netitems.nrest.IReportData) => {
        const {Columns, Title, General, Tabular, InUse, ReportType, OuterJoin} = data;
        if (Columns) {
            this.columns = prepareExpressionsToStore(Columns);
            if (Columns.length > 0) {
                const expressions = Columns.filter(column => column.IsExpression);
                const expressionsIds = expressions.map(exp => Number(exp.ColId));
                this.setExpressionIds(expressionsIds);
            }
        } else {
            this.columns = [];
        }
        this.title = Title;
        this.isGeneral = General;
        this.isUseAsDataset = ReportType === priority.netitems.nrest.RepType.RepType_DataSet;
        this.isTabular = Tabular;
        this.isActive = InUse;
        this.outerJoinId = Number(OuterJoin);
        this.savedOuterJoinId = Number(OuterJoin);
    };

    @computed get isMultiCompany(): boolean {
        return this.columns?.some(col => col.IsCompany);
    }

    setColumns = (columns: ReportColumn[]) => {
        this.columns = columns;
    };

    setLinkId = (linkId: string) => {
        this.linkId = linkId;
    };

    setAxisModeData = value => {
        this.aXisModeData = value;
    };

    @action
    updateColumns = (columns: ReportColumn[] = []) => {
        if (this.columns?.length > 0) {
            const existingColumns = this.columns.reduce((acc, value) => {
                const existingCol = columns.find(col => col.NodeId === value.NodeId);
                if (value.IsCompany || value.IsExpression || existingCol) {
                    acc.push(value);
                }
                return acc;
            }, []);

            const newColumns = columns.filter(
                column => !existingColumns.some(col => col.NodeId === column.NodeId)
            );

            this.columns = [...existingColumns, ...newColumns];
        } else {
            this.columns = columns;
        }

        this.updateDirty();
    };

    @action
    setSelectedNodes = (selected: Map<string, priority.netitems.nrest.ITreeNode>) => {
        this.selectedNodes = selected;
    };
    @action
    initSelectedNodes = (selected: Map<string, priority.netitems.nrest.ITreeNode>) => {
        if (!this.selectedNodes) {
            this.selectedNodes = selected;
        }
    };

    addColumn = (column: ReportColumn) => {
        this.columns.push(column);
        this.updateDirty();
    };

    @action
    addExpression = (ColId: number) => {
        this.addColumn({
            Width: 20,
            IsExpression: true,
            ColId,
            Type: priority.netitems.form.ColumnType.Real,
            Title: texts.expression,
            NodeId: this.nodeId,
            OrigTitle: texts.expression,
            error: null,
            SortType: priority.netitems.nrest.RepColSortType.RepColSortType_Ascending
        });
    };
    @action
    setReportTitle = (title: string) => {
        this.title = title;
        this.updateDirty();
    };

    @action
    setReportId = (id: number | Long) => {
        this.reportId = id;
    };

    @action
    setNodeId = (nodeId: string) => {
        this.nodeId = nodeId;
        this.resetOuterJoinMap(nodeId);
    };

    resetOuterJoinMap = nodeId => {
        this.outerJoinMap = initOuterJoinMap(nodeId);
    };

    @action
    toggleIsTabular = () => {
        this.isTabular = !this.isTabular;
        this.updateDirty();
    };

    @action
    toggleIsActive = () => {
        this.isActive = !this.isActive;
        this.updateDirty();
    };

    @action
    toggleIsGeneral = () => {
        this.isGeneral = !this.isGeneral;
        this.updateDirty();
    };

    @action
    toggleUseAsDataset = () => {
        this.isUseAsDataset = !this.isUseAsDataset;
        this.updateDirty();
    };

    @action
    setUseAsChart = () => {
        this.isUseAsChart = true;
        this.updateDirty();
    };

    @action
    toggleIsMultiCompany = () => {
        if (!this.isMultiCompany) {
            this.addColumn({
                Width: 48,
                IsCompany: true,
                Title: texts.companyName,
                OrigTitle: texts.companyName,
                ColId: 0,
                SortType: priority.netitems.nrest.RepColSortType.RepColSortType_Ascending
            });
        } else {
            const indexToDelete = this.columns.findIndex(col => col.IsCompany);
            if (indexToDelete > -1) {
                this.deleteColumn(indexToDelete);
            }
        }
    };
    @action
    updateColumn = (index: number, key: string, value: string | boolean | number) => {
        const columns = [...this.columns];

        if (key === ColumnsFieldsNames.AxisMode) {
            const foundIndex = columns.findIndex(column => column.AxisMode === value);
            if (foundIndex > -1) {
                columns[foundIndex].AxisMode =
                    priority.netitems.nrest.RepColAxisMode.RepColAxisMode_None;
            }
        }

        if (key === ColumnsFieldsNames.Sort && value === 0) {
            columns[index][key] = value;
        }

        columns[index][key] = value;
        if (columns[index].error) {
            delete columns[index].error;
        }
        if (key === ColumnsFieldsNames.AxisMode) {
            let hasXaxisItem = false;
            let hasSortXaisItem;

            columns.forEach((column, idx) => {
                if (column.AxisMode === priority.netitems.nrest.RepColAxisMode.RepColAxisMode_X) {
                    hasXaxisItem = true;
                }

                if (
                    column.AxisMode ===
                    priority.netitems.nrest.RepColAxisMode.RepColAxisMode_OrderBy
                ) {
                    hasSortXaisItem = idx;
                }
            });
            if (!hasXaxisItem && hasSortXaisItem !== undefined) {
                columns[hasSortXaisItem][key] =
                    priority.netitems.nrest.RepColAxisMode.RepColAxisMode_None;
            }
        }

        this.columns = columns;
        this.updateDirty();
    };

    @action
    deleteColumn = (index: number) => {
        const col = this.columns[index];
        this.columns.splice(index, 1);
        this.selectedNodes.delete(col.NodeId);
        this.updateDirty();
    };

    @action
    setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    @action reorderColumns = items => {
        this.columns = items;
        this.selectedNodes = new Map(
            [...this.selectedNodes].sort(
                (a, b) =>
                    this.columns.findIndex(item => item.NodeId === a[1].NodeId) -
                    this.columns.findIndex(item => item.NodeId === b[1].NodeId)
            )
        );
        this.updateDirty();
    };

    @action
    resetDirty = () => {
        this.isDirty = false;
    };

    updateDirty = () => {
        if (!this.isDirty) {
            this.isDirty = true;
        }
    };

    setDuplicate = (id: number | Long) => {
        this.duplicateId = id;
    };

    resetIsNew = () => {
        this.isNew = false;
    };

    @action
    setExpressionIds = (ids: Array<number>) => {
        this.expressionIds = ids;
    };

    @action
    addExpressionId = (id: number) => {
        this.expressionIds.push(id);
    };

    getExpressionIds = () => {
        return this.expressionIds;
    };

    setOuterJoinMap = (map: TreeNodeProps[]) => {
        this.outerJoinMap = map;
    };

    getOuterJoinMap = () => {
        return this.outerJoinMap;
    };

    @action
    setOuterJoinId = (id: number) => {
        this.outerJoinId = id;
    };

    getOuterJoinId = () => {
        return this.outerJoinId;
    };

    @action
    setSavedOuterJoinId = (id: number) => {
        this.savedOuterJoinId = id;
        this.updateDirty();
    };
}
